import { UseMutationResult, useQueryClient } from '@tanstack/react-query';

import {
  useDeleteCustomBenchmark,
  usePatchCustomBenchmark,
  usePostCustomBenchmark,
} from '@/api/rest/resources/benchmark';
import { CustomBenchmark } from '@/api/rest/resources/types/benchmark';
import { Logger } from '@/lib/logs/logger';
import { assertIsDefined } from '@/utils/assert';

import { customBenchmarkKeys } from './useCustomBenchmarks';

export const useCreateCustomBenchmark = () => {
  const mutation = usePostCustomBenchmark();
  return useCustomBenchmarkMutation(mutation);
};

export const useUpdateCustomBenchmark = () => {
  const mutation = usePatchCustomBenchmark();
  return useCustomBenchmarkMutation(mutation);
};

export const useRemoveCustomBenchmark = () => {
  const mutation = useDeleteCustomBenchmark();
  return useCustomBenchmarkMutation(mutation);
};

const useCustomBenchmarkMutation = <TData, TVariables>(
  mutation: UseMutationResult<TData, Error, TVariables, unknown>,
) => {
  const queryClient = useQueryClient();

  const submit = async (
    organizationId: string,
    benchmarkId?: string,
    data?: Partial<CustomBenchmark>,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    assertIsDefined(organizationId, 'organizationId');

    const variables = {
      pathVariables: {
        organization_id: organizationId,
        ...(benchmarkId ? { pk: benchmarkId } : {}),
      },
      queryParams: undefined,
      ...(data ? { bodyData: data } : {}),
    } as unknown as TVariables;

    try {
      await mutation.mutateAsync(variables);
      queryClient.invalidateQueries({ queryKey: customBenchmarkKeys.all(organizationId) });
      onSuccess?.();
    } catch (error) {
      Logger.error(`useCustomBenchmarkMutation error: ${error}`);
      onError?.(error as Error);
    }
  };

  return { submit, isSubmitting: mutation.isPending };
};
